import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { getCompany, getCompanyReviews, createReview, createQuoteRequest, getCompanyPosts, getCompanyStories, followCompany, unfollowCompany, checkFollowing } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { da } from 'date-fns/locale';
import { 
  MapPin, Phone, Mail, CheckCircle2, ArrowLeft, 
  Send, Star, Calendar as CalendarIcon, MessageSquare,
  Award, Clock, CreditCard, UserPlus, UserMinus, Image as ImageIcon, X, Heart, MessageCircle,
  ChevronLeft, ChevronRight, Copy
} from 'lucide-react';
import StarRating from '@/components/StarRating';
import { logger } from '@/lib/logger';

export default function CompanyProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, token } = useAuth();
  const [company, setCompany] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [quoteForm, setQuoteForm] = useState({ description: '' });
  const [reviewForm, setReviewForm] = useState({ rating: 0, title: '', comment: '' });
  const [posts, setPosts] = useState([]);
  const [stories, setStories] = useState([]);
  const [isFollowing, setIsFollowing] = useState(false);
  const [activeStory, setActiveStory] = useState(null);
  const [storyIdx, setStoryIdx] = useState(0);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [activePost, setActivePost] = useState(null);
  const [activeImageIdx, setActiveImageIdx] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [companyRes, reviewsRes, postsRes, storiesRes] = await Promise.all([
          getCompany(id),
          getCompanyReviews(id),
          getCompanyPosts(id),
          getCompanyStories(id)
        ]);
        setCompany(companyRes.data);
        setReviews(reviewsRes.data);
        setPosts(postsRes.data);
        setStories(storiesRes.data);
      } catch (error) {
        logger.error('Error fetching company:', error);
        toast.error('Kunne ikke hente virksomhedsdata');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  useEffect(() => {
    const checkFollow = async () => {
      if (token && user?.user_type === 'user') {
        try {
          const res = await checkFollowing(id);
          setIsFollowing(res.data.following);
        } catch (e) { logger.error('checkFollowing failed', e); }
      }
    };
    checkFollow();
  }, [id, token, user]);

  // Auto-open the quote dialog when the URL carries ?book=1 (e.g. from the map "Book nu" button).
  // If the user is not logged in, redirect them to /login first with intent=quote.
  useEffect(() => {
    if (searchParams.get('book') !== '1') return;
    if (!company) return;
    if (!user) {
      navigate('/login', { state: { redirectTo: `/company/${id}?book=1`, intent: 'quote' } });
      return;
    }
    if (user.user_type === 'user') {
      setQuoteDialogOpen(true);
    }
    // Clear the query param so refresh doesn't re-open
    const next = new URLSearchParams(searchParams);
    next.delete('book');
    setSearchParams(next, { replace: true });
  }, [searchParams, company, user, id, navigate, setSearchParams]);

  const openPost = (post) => {
    setActiveImageIdx(0);
    setActivePost(post);
  };

  const closePost = () => {
    setActivePost(null);
    setActiveImageIdx(0);
  };

  const postImages = activePost ? (activePost.images && activePost.images.length > 0 ? activePost.images : [activePost.image]).filter(Boolean) : [];

  const nextImage = (e) => {
    e?.stopPropagation();
    if (postImages.length <= 1) return;
    setActiveImageIdx((idx) => (idx + 1) % postImages.length);
  };

  const prevImage = (e) => {
    e?.stopPropagation();
    if (postImages.length <= 1) return;
    setActiveImageIdx((idx) => (idx - 1 + postImages.length) % postImages.length);
  };

  useEffect(() => {
    if (!activePost) return;
    const onKey = (e) => {
      if (e.key === 'ArrowRight') nextImage();
      else if (e.key === 'ArrowLeft') prevImage();
      else if (e.key === 'Escape') closePost();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activePost, postImages.length]);

  // Stories auto-advance every 4 seconds
  useEffect(() => {
    if (!activeStory || stories.length === 0) return;
    const t = setTimeout(() => {
      const next = storyIdx + 1;
      if (next >= stories.length) {
        setActiveStory(null);
        setStoryIdx(0);
      } else {
        setStoryIdx(next);
        setActiveStory(stories[next]);
      }
    }, 4000);
    return () => clearTimeout(t);
  }, [activeStory, storyIdx, stories]);

  const handleFollowToggle = async () => {
    if (!token) { toast.error('Log ind for at følge'); navigate('/login'); return; }
    try {
      if (isFollowing) {
        await unfollowCompany(id);
        setIsFollowing(false);
        toast.success('Du følger ikke længere virksomheden');
      } else {
        await followCompany(id);
        setIsFollowing(true);
        toast.success('Du følger nu virksomheden');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke opdatere følge-status');
    }
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error('Log ind for at anmode om tilbud');
      navigate('/login');
      return;
    }

    setSubmitting(true);
    try {
      await createQuoteRequest({
        company_id: id,
        description: quoteForm.description,
        preferred_date: selectedDate ? format(selectedDate, 'yyyy-MM-dd') : null
      });
      toast.success('Tilbudsanmodning sendt!');
      setQuoteDialogOpen(false);
      setQuoteForm({ description: '' });
      setSelectedDate(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke sende anmodning');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error('Log ind for at skrive en anmeldelse');
      navigate('/login');
      return;
    }

    setSubmitting(true);
    try {
      const response = await createReview({
        company_id: id,
        rating: reviewForm.rating,
        title: reviewForm.title,
        comment: reviewForm.comment
      });
      setReviews([response.data, ...reviews]);
      // Refresh company to get updated rating
      const companyRes = await getCompany(id);
      setCompany(companyRes.data);
      toast.success('Anmeldelse oprettet!');
      setReviewDialogOpen(false);
      setReviewForm({ rating: 5, title: '', comment: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke oprette anmeldelse');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 py-8">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <Skeleton className="h-64 w-full rounded-xl mb-8" />
          <Skeleton className="h-8 w-1/3 mb-4" />
          <Skeleton className="h-4 w-2/3 mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
          </div>
        </div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Virksomhed ikke fundet</h2>
          <Button onClick={() => navigate('/directory')}>
            Tilbage til oversigt
          </Button>
        </div>
      </div>
    );
  }

  const defaultImage = 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&h=400&fit=crop';

  return (
    <div className="min-h-screen bg-stone-50" data-testid="company-profile-page">
      {/* Hero Section */}
      <div className="relative h-64 md:h-80">
        <img 
          src={company.photos?.[0] || defaultImage}
          alt={company.company_name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
        
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 text-white hover:bg-white/20"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Tilbage
        </Button>

        <div className="absolute bottom-6 left-6 right-6 flex items-end gap-4">
          {/* Profile picture (logo) */}
          {company.logo && (
            <div className="hidden sm:block flex-shrink-0">
              <div className="w-24 h-24 md:w-28 md:h-28 rounded-2xl bg-white shadow-2xl ring-4 ring-white/40 p-2 overflow-hidden">
                <img 
                  src={company.logo} 
                  alt={`${company.company_name} logo`}
                  className="w-full h-full object-contain"
                  data-testid="company-logo"
                />
              </div>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap gap-2 mb-3">
              {company.category && (
                <Badge className="bg-stone-100 text-stone-800">
                  {company.category}
                </Badge>
              )}
              {company.verified && (
                <Badge className="bg-green-100 text-green-800 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  Verificeret
                </Badge>
              )}
              <Badge className="bg-primary/90 text-white capitalize">
                {company.subscription_tier} abonnement
              </Badge>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
              {company.company_name}
            </h1>
            <div className="flex items-center gap-4 text-white/90">
              <StarRating rating={company.trustpilot_rating} size="lg" />
              <span className="text-sm">({company.review_count} anmeldelser)</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-8">
        {/* Follow button */}
        {user?.user_type === 'user' && (
          <div className="flex justify-end mb-4">
            <Button
              variant={isFollowing ? "outline" : "default"}
              onClick={handleFollowToggle}
              data-testid="follow-button"
            >
              {isFollowing ? <UserMinus className="w-4 h-4 mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
              {isFollowing ? 'Følger' : 'Følg'}
            </Button>
          </div>
        )}

        {/* Stories */}
        {stories.length > 0 && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-3">Stories</h3>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {stories.map((story, idx) => (
                <button
                  key={story.id}
                  onClick={() => { setStoryIdx(idx); setActiveStory(story); }}
                  className="flex-shrink-0 w-20 h-20 rounded-full p-[3px] bronze-shine transition-transform hover:scale-105"
                  data-testid={`story-${story.id}`}
                >
                  <span className="block w-full h-full rounded-full overflow-hidden bg-white p-[2px]">
                    <img src={story.image} alt={story.caption || 'Story'} className="w-full h-full object-cover rounded-full" />
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Story viewer - Instagram-style fullscreen */}
        {activeStory && (
          <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center" onClick={() => setActiveStory(null)} data-testid="story-viewer">
            {/* Prev/Next click zones */}
            {stories.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); const ni = (storyIdx - 1 + stories.length) % stories.length; setStoryIdx(ni); setActiveStory(stories[ni]); }}
                  className="absolute left-0 top-0 bottom-0 w-1/4 z-40 group flex items-center justify-start pl-6"
                  data-testid="story-prev-button"
                  aria-label="Forrige story"
                >
                  <ChevronLeft className="w-10 h-10 text-white/60 opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); const ni = (storyIdx + 1) % stories.length; setStoryIdx(ni); setActiveStory(stories[ni]); }}
                  className="absolute right-0 top-0 bottom-0 w-1/4 z-40 group flex items-center justify-end pr-6"
                  data-testid="story-next-button"
                  aria-label="Næste story"
                >
                  <ChevronRight className="w-10 h-10 text-white/60 opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
              </>
            )}

            {/* Story frame - 9:16 portrait, contained */}
            <div
              className="relative w-full max-w-[420px] mx-4 rounded-2xl overflow-hidden shadow-2xl"
              style={{ aspectRatio: '9 / 16', background: '#000' }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Background blurred image fills frame */}
              <div 
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${activeStory.image})`, filter: 'blur(32px) brightness(0.6)', transform: 'scale(1.15)' }}
                aria-hidden="true"
              />
              {/* Main contained image */}
              <img 
                src={activeStory.image} 
                alt={activeStory.caption || 'Story'} 
                className="absolute inset-0 w-full h-full object-contain z-10"
              />

              {/* Progress bars on top */}
              <div className="absolute top-3 left-3 right-3 flex gap-1 z-30">
                {stories.map((s, i) => (
                  <div key={s.id || `story-${i}`} className="flex-1 h-[3px] bg-white/30 rounded-full overflow-hidden">
                    <div
                      className={`h-full bg-white ${i === storyIdx ? 'story-progress-bar' : i < storyIdx ? 'w-full' : 'w-0'}`}
                      style={i === storyIdx ? { animationDuration: '4s' } : {}}
                    />
                  </div>
                ))}
              </div>

              {/* Header */}
              <div className="absolute top-8 left-3 right-3 flex items-center gap-3 z-30">
                <div className="w-9 h-9 rounded-full overflow-hidden p-[2px] bronze-shine flex-shrink-0">
                  <div className="w-full h-full rounded-full bg-gradient-to-br from-[#112110] to-[#c97f59] flex items-center justify-center text-white text-xs font-bold">
                    {(company?.company_name || 'V').charAt(0)}
                  </div>
                </div>
                <div className="flex-1 min-w-0 text-white">
                  <p className="text-sm font-semibold leading-tight truncate drop-shadow">{company?.company_name}</p>
                  <p className="text-[11px] text-white/80 drop-shadow">
                    {new Date(activeStory.created_at || Date.now()).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
                <button 
                  onClick={(e) => { e.stopPropagation(); setActiveStory(null); }}
                  className="text-white p-1 hover:bg-white/10 rounded-full transition-colors"
                  data-testid="close-story"
                  aria-label="Luk"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Caption + footer */}
              {activeStory.caption && (
                <div className="absolute bottom-0 left-0 right-0 p-4 pb-6 bg-gradient-to-t from-black/80 via-black/40 to-transparent z-20">
                  <p className="text-white text-sm leading-snug">{activeStory.caption}</p>
                </div>
              )}
            </div>

            {/* Counter outside frame */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/60 text-xs z-30">
              {storyIdx + 1} / {stories.length}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Description */}
            <Card>
              <CardHeader>
                <CardTitle>Om virksomheden</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground leading-relaxed">
                  {company.description}
                </p>
              </CardContent>
            </Card>

            {/* Posts Feed - Instagram Grid Style */}
            {posts.length > 0 && (
              <Card data-testid="posts-section">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ImageIcon className="w-5 h-5" />
                    Opslag ({posts.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-1 sm:gap-2">
                    {posts.map((post) => {
                      const imgs = post.images && post.images.length > 0 ? post.images : [post.image];
                      return (
                        <button
                          key={post.id}
                          onClick={() => openPost(post)}
                          className="relative aspect-square overflow-hidden bg-stone-100 group cursor-pointer rounded-sm"
                          data-testid={`post-${post.id}`}
                        >
                          <img 
                            src={imgs[0]} 
                            alt={post.caption || 'Opslag'} 
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                          />
                          {imgs.length > 1 && (
                            <div className="absolute top-2 right-2 bg-black/50 backdrop-blur-sm rounded-full p-1.5" data-testid={`multi-image-${post.id}`}>
                              <Copy className="w-3.5 h-3.5 text-white" />
                            </div>
                          )}
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
                        </button>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Post viewer modal - Instagram style with carousel */}
            {activePost && (
              <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={closePost} data-testid="post-modal">
                <button onClick={closePost} className="absolute top-4 right-4 text-white z-50" data-testid="close-post">
                  <X className="w-8 h-8" />
                </button>
                <div className="bg-white rounded-xl overflow-hidden max-w-4xl w-full max-h-[90vh] flex flex-col md:flex-row" onClick={(e) => e.stopPropagation()}>
                  <div className="md:w-3/5 bg-black flex items-center justify-center relative">
                    <img 
                      src={postImages[activeImageIdx]} 
                      alt={activePost.caption || 'Opslag'} 
                      className="w-full h-full max-h-[90vh] object-contain"
                      data-testid="post-modal-image"
                    />
                    {postImages.length > 1 && (
                      <>
                        <button
                          onClick={prevImage}
                          className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white text-black flex items-center justify-center shadow-lg transition-all hover:scale-105"
                          aria-label="Forrige billede"
                          data-testid="post-prev-button"
                        >
                          <ChevronLeft className="w-6 h-6" />
                        </button>
                        <button
                          onClick={nextImage}
                          className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white text-black flex items-center justify-center shadow-lg transition-all hover:scale-105"
                          aria-label="Næste billede"
                          data-testid="post-next-button"
                        >
                          <ChevronRight className="w-6 h-6" />
                        </button>
                        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 bg-black/50 backdrop-blur-sm px-3 py-1.5 rounded-full">
                          {postImages.map((img, i) => (
                            <button
                              key={typeof img === 'string' ? img : `img-${i}`}
                              onClick={(e) => { e.stopPropagation(); setActiveImageIdx(i); }}
                              className={`rounded-full transition-all ${i === activeImageIdx ? 'w-2 h-2 bg-white' : 'w-1.5 h-1.5 bg-white/50 hover:bg-white/80'}`}
                              aria-label={`Gå til billede ${i + 1}`}
                              data-testid={`post-dot-${i}`}
                            />
                          ))}
                        </div>
                        <div className="absolute top-3 right-3 bg-black/60 text-white text-xs font-medium px-2.5 py-1 rounded-full">
                          {activeImageIdx + 1} / {postImages.length}
                        </div>
                      </>
                    )}
                  </div>
                  <div className="md:w-2/5 flex flex-col">
                    <div className="flex items-center gap-3 px-5 py-4 border-b">
                      <div className="w-10 h-10 rounded-full overflow-hidden ring-2 ring-[#c97f59]/30 flex-shrink-0">
                        <div className="w-full h-full bg-gradient-to-br from-[#112110] to-[#c97f59] flex items-center justify-center text-white text-sm font-bold">
                          {(activePost.company_name || 'V').charAt(0)}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm">{activePost.company_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(activePost.created_at).toLocaleDateString('da-DK', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </p>
                      </div>
                    </div>
                    <div className="flex-1 px-5 py-4 overflow-y-auto">
                      {activePost.caption && (
                        <p className="text-sm leading-relaxed">
                          <span className="font-semibold mr-1.5">{activePost.company_name}</span>
                          {activePost.caption}
                        </p>
                      )}
                    </div>
                    <div className="h-[2px] bronze-shine" />
                  </div>
                </div>
              </div>
            )}

            {/* Photo viewer modal */}
            {selectedPhoto && (
              <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center" onClick={() => setSelectedPhoto(null)}>
                <button onClick={() => setSelectedPhoto(null)} className="absolute top-4 right-4 text-white"><X className="w-8 h-8" /></button>
                <img src={selectedPhoto} alt="Billede" className="max-w-[90vw] max-h-[90vh] rounded-lg" />
              </div>
            )}

            {/* Certifications */}
            {company.certifications?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Award className="w-5 h-5" />
                    Certificeringer
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {company.certifications.map((cert, index) => (
                      <Badge key={`${cert}-${index}`} variant="outline" className="px-3 py-1">
                        {cert}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Prices */}
            {company.prices?.length > 0 && (
              <Card data-testid="company-prices">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5" />
                    Priser
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {company.prices.map((price, index) => (
                      <div key={`${price.service}-${index}`} className="flex items-center justify-between py-2 border-b last:border-0">
                        <span className="font-medium">{price.service}</span>
                        <span className="text-primary font-semibold">{price.amount} kr</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Nominations */}
            {company.nominations?.length > 0 && (
              <Card data-testid="company-nominations">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Award className="w-5 h-5 text-[#c97f59]" />
                    Nomineringer & Priser
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {company.nominations.map((nom, index) => (
                      <div key={`${nom}-${index}`} className="flex items-center gap-2 p-2 bg-[#faf3ec] rounded-lg">
                        <Award className="w-4 h-4 text-[#9a5c3a] flex-shrink-0" />
                        <span className="text-sm">{nom}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar - Sticky with Trustpilot, Contact, Reviews */}
          <div className="space-y-6">
            <div className="sticky top-24 space-y-6 max-h-[calc(100vh-8rem)] overflow-y-auto scrollbar-thin">
              {/* Trustpilot / Rating Card */}
              <Card className="border-[#c97f59]/20">
                <CardContent className="pt-6">
                  <div className="text-center space-y-3">
                    <div className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bronze-shine text-white text-xs font-semibold">
                      <Star className="w-3.5 h-3.5 fill-white" />
                      Anmeldelser
                    </div>
                    <div className="flex items-center justify-center gap-2">
                      <span className="text-3xl font-bold bronze-text">{company.trustpilot_rating?.toFixed(1) || '0.0'}</span>
                      <span className="text-muted-foreground text-sm">/ 5</span>
                    </div>
                    <StarRating rating={company.trustpilot_rating || 0} size="lg" showValue={false} />
                    <p className="text-sm text-muted-foreground">{company.review_count || 0} anmeldelser</p>
                    {user?.user_type === 'user' && (
                      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
                        <DialogTrigger asChild>
                          <Button variant="outline" size="sm" className="w-full mt-2" data-testid="write-review-button">
                            <Star className="w-4 h-4 mr-2" />
                            Skriv anmeldelse
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Skriv en anmeldelse</DialogTitle>
                          </DialogHeader>
                          <form onSubmit={handleReviewSubmit} className="space-y-4">
                            <div className="space-y-2">
                              <Label>Bedømmelse (halve stjerner muligt)</Label>
                              <StarRating 
                                rating={reviewForm.rating} 
                                size="xl" 
                                interactive 
                                showValue
                                onRate={(rating) => setReviewForm({ ...reviewForm, rating })}
                              />
                              {reviewForm.rating === 0 && (
                                <p className="text-xs text-red-500">Vælg mindst 0,5 stjerne</p>
                              )}
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="review-title">Titel</Label>
                              <Input id="review-title" value={reviewForm.title} onChange={(e) => setReviewForm({ ...reviewForm, title: e.target.value })} placeholder="Kort overskrift..." required data-testid="review-title-input" />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="review-comment">Kommentar (mindst 10 tegn)</Label>
                              <Textarea id="review-comment" value={reviewForm.comment} onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })} placeholder="Del din oplevelse..." required minLength={10} data-testid="review-comment-input" />
                              <p className="text-xs text-muted-foreground">{reviewForm.comment.length}/10 tegn minimum</p>
                            </div>
                            <Button type="submit" className="w-full" disabled={submitting || reviewForm.rating === 0 || reviewForm.comment.length < 10} data-testid="submit-review-button">
                              {submitting ? 'Sender...' : 'Send anmeldelse'}
                            </Button>
                          </form>
                        </DialogContent>
                      </Dialog>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Contact Card */}
              <Card>
                <CardHeader>
                  <CardTitle>Kontakt</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 text-sm">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                        <MapPin className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-muted-foreground">Adresse</p>
                        <p className="font-medium">{company.address}, {company.city}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Phone className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-muted-foreground">Telefon</p>
                        <p className="font-medium">{company.phone}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Mail className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-muted-foreground">Email</p>
                        <p className="font-medium">{company.email}</p>
                      </div>
                    </div>
                  </div>
                  <div className="pt-4 border-t">
                    {!user ? (
                      <Button
                        className="w-full bronze-shine border-0 text-white"
                        size="lg"
                        onClick={() => navigate('/login', { state: { redirectTo: `/company/${id}`, intent: 'quote' } })}
                        data-testid="request-quote-button"
                      >
                        <Send className="w-5 h-5 mr-2" />
                        Anmod om tilbud
                      </Button>
                    ) : (
                    <Dialog open={quoteDialogOpen} onOpenChange={setQuoteDialogOpen}>
                      <DialogTrigger asChild>
                        <Button className="w-full bronze-shine border-0 text-white" size="lg" data-testid="request-quote-button">
                          <Send className="w-5 h-5 mr-2" />
                          Anmod om tilbud
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Anmod om tilbud fra {company.company_name}</DialogTitle>
                          <DialogDescription>
                            Beskriv dit projekt og vælg en ønsket startdato. {company.company_name} vender tilbage med et uforpligtende tilbud.
                          </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleQuoteSubmit} className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="quote-description">Beskriv dit projekt</Label>
                            <Textarea id="quote-description" value={quoteForm.description} onChange={(e) => setQuoteForm({ ...quoteForm, description: e.target.value })} placeholder="Fortæl os om dit projekt..." className="min-h-[120px]" required data-testid="quote-description-input" />
                          </div>
                          <div className="space-y-2">
                            <Label>Foretrukken dato (valgfri)</Label>
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button variant="outline" className="w-full justify-start" data-testid="date-picker-trigger">
                                  <CalendarIcon className="w-4 h-4 mr-2" />
                                  {selectedDate ? format(selectedDate, 'PPP', { locale: da }) : 'Vælg dato'}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0">
                                <Calendar mode="single" selected={selectedDate} onSelect={setSelectedDate} locale={da} disabled={(date) => date < new Date()} />
                              </PopoverContent>
                            </Popover>
                          </div>
                          <Button type="submit" className="w-full" disabled={submitting} data-testid="submit-quote-button">
                            {submitting ? 'Sender...' : 'Send anmodning'}
                          </Button>
                        </form>
                      </DialogContent>
                    </Dialog>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Reviews in sidebar */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="w-5 h-5" />
                    Anmeldelser ({reviews.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {reviews.length > 0 ? (
                    <div className="space-y-4">
                      {reviews.map((review) => (
                        <div key={review.id} className="border-b border-border/50 pb-4 last:border-0 last:pb-0">
                          <div className="flex items-start justify-between mb-1">
                            <div>
                              <p className="font-semibold text-sm">{review.user_name}</p>
                              <StarRating rating={review.rating} size="sm" showValue={false} />
                            </div>
                            <span className="text-xs text-muted-foreground">
                              {new Date(review.created_at).toLocaleDateString('da-DK')}
                            </span>
                          </div>
                          <h4 className="font-medium text-sm mb-1">{review.title}</h4>
                          <p className="text-muted-foreground text-sm">{review.comment}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-center py-4 text-sm">
                      Ingen anmeldelser endnu
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
